import { computed } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useApi, useToaster } from '@shopexperts/shared';
import type { SubmitReviewByRequestIdBody } from '@shopexperts/shared';

/**
 * Composable for submitting reviews as an authenticated client
 * Handles review submission by request ID with error and success notifications
 */
export function useSubmitClientReview() {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const submitMutation = useMutation({
    mutationKey: ['client', 'submitReview'],
    mutationFn: async (payload: SubmitReviewByRequestIdBody) => {
      return await api.client.reviewsV2.submitReviewByRequestId({
        body: payload
      });
    },
    onSuccess: () => {
      toaster.create({
        title: 'Review submitted successfully!',
        description: 'Thank you for taking the time to share your feedback. Your review helps other clients make informed decisions.',
        type: 'success',
      });
      // Invalidate review requests and reviews queries to refresh the lists
      queryClient.invalidateQueries({ queryKey: ['client', 'review-requests'] });
      queryClient.invalidateQueries({ queryKey: ['client', 'reviews'] });
    },
    onError: (error: any) => {
      console.error('[useSubmitClientReview] Error:', error);
      
      const errorMessage = 
        error?.response?.data?.message || 
        error?.response?.data?.error ||
        error?.message ||
        'Failed to submit review. Please try again.';
      
      toaster.create({
        title: 'Error submitting review',
        description: errorMessage,
        type: 'error',
      });
    },
  });

  const isSubmitting = computed(() => submitMutation.isPending.value);
  const isSuccess = computed(() => submitMutation.isSuccess.value);
  const isError = computed(() => submitMutation.isError.value);

  const submitReview = (payload: SubmitReviewByRequestIdBody) => {
    return submitMutation.mutateAsync(payload);
  };

  const reset = () => {
    submitMutation.reset();
  };

  return {
    submitMutation,
    isSubmitting,
    isSuccess,
    isError,
    submitReview,
    reset,
  };
}

